/**
 * Comprime a foto antes de ela sair do aparelho.
 *
 * Sem isto, a foto simplesmente nao passa. A camera do celular entrega quadros
 * de 1920x1080 ou mais, e `canvas.toDataURL('image/png')` sobre isso da varios
 * MB; base64 ainda infla 33%, e o `express.json` do backend aceita 1mb. O
 * porteiro veria "erro ao enviar" sem nunca entender o motivo.
 *
 * JPEG e nao PNG de proposito: PNG e sem perda e otimo para o traco da
 * assinatura, que tem duas cores e bordas duras. Foto de papelao sob luz de
 * portaria e o caso oposto — textura e gradiente —, onde PNG fica enorme e o
 * JPEG resolve em uma fracao do tamanho sem diferenca visivel para a pergunta
 * que a foto responde: a caixa chegou amassada ou nao.
 */

/** Acima disto nao se enxerga mais avaria; so se paga banda. */
const MAX_DIMENSION = 1280;
/** Qualidade que mantem legivel a etiqueta e o vinco da caixa. */
const QUALITY = 0.7;

export interface CompressedImage {
  dataUrl: string;
  /** Bytes reais depois de decodificar o base64 — o que o servidor vai medir. */
  byteSize: number;
  width: number;
  height: number;
}

/** Bytes que a data URL representa, sem materializar o buffer. */
function byteSizeOf(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/**
 * Reduz e comprime, preservando a proporcao.
 *
 * Imagem que ja cabe nao e ampliada: reamostrar para cima nao acrescenta
 * detalhe nenhum e so custa bytes.
 */
export function compressImage(source: HTMLCanvasElement): CompressedImage | null {
  if (source.width === 0 || source.height === 0) return null;

  const scale = Math.min(1, MAX_DIMENSION / Math.max(source.width, source.height));

  const output = document.createElement('canvas');
  output.width = Math.max(1, Math.round(source.width * scale));
  output.height = Math.max(1, Math.round(source.height * scale));

  const context = output.getContext('2d');
  if (!context) return null;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, output.width, output.height);

  const dataUrl = output.toDataURL('image/jpeg', QUALITY);
  // Navegador sem suporte a JPEG no canvas devolve PNG calado, e ai o tamanho
  // volta a ser o problema que este arquivo existe para evitar.
  if (!dataUrl.startsWith('data:image/jpeg')) return null;

  return {
    dataUrl,
    byteSize: byteSizeOf(dataUrl),
    width: output.width,
    height: output.height,
  };
}
